import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import HoldingChart from '../features/portfolio/components/HoldingChart';
import HoldingTransactionsTable from '../features/portfolio/components/HoldingTransactionsTable';
import CustomDateInput from '../components/ui/CustomDateInput';
import CustomTimeInput from '../components/ui/CustomTimeInput';
import CustomSelect from '../components/ui/CustomSelect';
import { uiStyles } from '../theme/uiStyles';
import { slugify, toArray } from '../utils/slugify';
import { apiFetch } from '../api/apiClient';

const ASSET_TYPE_OPTIONS = [
  { value: 'Stock',     label: 'Stock' },
  { value: 'ETF',       label: 'ETF' },
  { value: 'Crypto',    label: 'Crypto' },
  { value: 'Bond',      label: 'Bond' },
  { value: 'Commodity', label: 'Commodity' },
  { value: 'Fund',      label: 'Fund' },
];

function HoldingDetailPage() {
  const { slug: slugParam, ticker: tickerParam } = useParams();
  const location = useLocation();
  const slug = slugParam || location.pathname.replace(/^\/portfolio\//, '').split('/')[0] || '';
  const ticker = tickerParam || location.pathname.replace(/^\/portfolio\//, '').split('/')[1] || '';
  const navigate = useNavigate();
  const [portfolioId, setPortfolioId] = useState(null);

  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [portfolio, setPortfolio] = useState(null);

  const [sellQuantity, setSellQuantity] = useState('');
  const [sellPrice, setSellPrice] = useState('');
  const [sellDate, setSellDate] = useState('');
  const [sellTime, setSellTime] = useState('12:00');
  const [sellNotes, setSellNotes] = useState('');

  const [buyQuantity, setBuyQuantity] = useState('');
  const [buyPrice, setBuyPrice] = useState('');
  const [buyDate, setBuyDate] = useState('');
  const [buyTime, setBuyTime] = useState('12:00');
  const [buyNotes, setBuyNotes] = useState('');

  const [chartPoints, setChartPoints] = useState([]);
  const [chartRange, setChartRange] = useState('all');
  const [chartLoading, setChartLoading] = useState(false);

  const [loading, setLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [localAssetType, setLocalAssetType] = useState('');
  const [localColor, setLocalColor] = useState('');

  const successTimeoutRef = useRef(null);

  const showSuccess = (message) => {
    setSuccessMessage(message);

    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
    }

    successTimeoutRef.current = setTimeout(() => {
      setSuccessMessage('');
    }, 3500);
  };

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  const fetchChartData = async (tickerSymbol, range) => {
    if (!tickerSymbol) return;
    try {
      setChartLoading(true);
      const res = await apiFetch(
        `/api/stock/history/${encodeURIComponent(tickerSymbol)}?range=${encodeURIComponent(range)}`
      );
      if (!res.ok) {
        setChartPoints([]);
        return;
      }
      const data = await res.json();
      setChartPoints(Array.isArray(data) ? data : []);
    } catch {
      setChartPoints([]);
    } finally {
      setChartLoading(false);
    }
  };

  const fetchPortfolio = async () => {
    if (!portfolioId) return;

    const response = await apiFetch(`/api/portfolios/${portfolioId}`);
    if (!response.ok) {
      throw new Error('Failed to load portfolio.');
    }

    const data = await response.json();
    setPortfolio(data);
  };

  const fetchSummary = async () => {
    if (!portfolioId) return;

    const response = await apiFetch(
      `/api/portfolio-v2/summary?portfolioId=${portfolioId}`
    );

    if (!response.ok) {
      throw new Error('Failed to load holding summary.');
    }

    const data = await response.json();
    setSummary(data);
  };

  const fetchTransactions = async () => {
    if (!portfolioId) return;

    const response = await apiFetch(
      `/api/PortfolioTransactions?portfolioId=${portfolioId}`
    );

    if (!response.ok) {
      throw new Error('Failed to load transactions.');
    }

    const data = await response.json();
    setTransactions(Array.isArray(data) ? data : []);
  };

  const refreshData = async () => {
    setLoading(true);
    setTransactionsLoading(true);
    setError('');

    try {
      await Promise.all([fetchPortfolio(), fetchSummary(), fetchTransactions()]);
    } catch (err) {
      setError(err.message || 'Failed to load holding detail.');
    } finally {
      setLoading(false);
      setTransactionsLoading(false);
    }
  };

  // Resolve slug (or legacy numeric id) -> numeric portfolio ID + canonical URL redirect
  useEffect(() => {
    let isMounted = true;
    const resolve = async () => {
      try {
        const res = await apiFetch(`/api/portfolios`);
        if (!res.ok) return;
        const data = await res.json();
        const list = toArray(data);
        const match =
          list.find((p) => slugify(p.name) === slug) ||
          list.find((p) => String(p.id) === String(slug)) ||
          null;
        if (!isMounted || !match) return;
        setPortfolioId(String(match.id));
        // Redirect legacy numeric-ID URLs to the canonical slug URL
        const correctSlug = slugify(match.name);
        if (correctSlug && correctSlug !== slug) {
          navigate(`/portfolio/${correctSlug}/${encodeURIComponent(ticker)}`, { replace: true });
        }
      } catch { /* ignore */ }
    };
    resolve();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);
  useEffect(() => {
    if (!portfolioId) return;
    refreshData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioId, ticker]);

  const holding = useMemo(() => {
    const allHoldings = Array.isArray(summary?.holdings) ? summary.holdings : [];
    return allHoldings.find(
      (item) => String(item.ticker || '').toUpperCase() === String(ticker || '').toUpperCase()
    );
  }, [summary, ticker]);

  // Načti uložený asset type z localStorage (nebo použij hodnotu z API)
  useEffect(() => {
    if (!portfolioId || !ticker) return;
    const key = `assetType:${portfolioId}:${String(ticker).toUpperCase()}`;
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        setLocalAssetType(stored);
        return;
      }
    } catch (_) {}
    if (holding?.assetType != null) {
      setLocalAssetType(String(holding.assetType));
    }
  }, [portfolioId, ticker, holding]);

  const handleAssetTypeChange = (value) => {
    setLocalAssetType(value);
    try {
      localStorage.setItem(`assetType:${portfolioId}:${String(ticker).toUpperCase()}`, value);
    } catch (_) {}
  };

  // Načti barvu z assetColors-{portfolioId} localStorage
  useEffect(() => {
    if (!portfolioId || !ticker) return;
    try {
      const stored = JSON.parse(localStorage.getItem(`assetColors-${portfolioId}`) || '{}');
      const color = stored[String(ticker).toUpperCase()];
      if (color) setLocalColor(color);
    } catch (_) {}
  }, [portfolioId, ticker]);

  const handleColorChange = (color) => {
    setLocalColor(color);
    try {
      const key = `assetColors-${portfolioId}`;
      const stored = JSON.parse(localStorage.getItem(key) || '{}');
      const t = String(ticker).toUpperCase();
      if (color) {
        stored[t] = color;
      } else {
        // Reset — remove the key entirely so the chart falls back to auto-assigned color
        delete stored[t];
      }
      localStorage.setItem(key, JSON.stringify(stored));
    } catch (_) {}
  };

  const holdingTransactions = useMemo(() => {
    return transactions.filter(
      (tx) =>
        String(tx?.instrument?.ticker || '').toUpperCase() === String(ticker || '').toUpperCase()
    );
  }, [transactions, ticker]);

  const reportCurrency = (summary?.baseCurrency || portfolio?.baseCurrency || 'USD').toUpperCase();
  const instrumentCurrency = (holding?.currency || holding?.tradeCurrency || 'USD').toUpperCase();

  const firstBuyDate = useMemo(() => {
    const buys = holdingTransactions
      .filter((tx) => tx.transactionType === 0)
      .map((tx) => (tx.occurredAt || '').split('T')[0])
      .filter(Boolean)
      .sort();
    return buys.length > 0 ? buys[0] : null;
  }, [holdingTransactions]);

  // Pick the tightest Yahoo Finance range that still covers the full period.
  // 1d  → 5-min candles  (passed through as-is)
  // 7d  → '5d' Yahoo Finance range, which gives the last 5 trading days at ~30-min intervals
  //        (5 trading days ≈ 7 calendar days; Yahoo does not have a native '7d' range)
  // 1mo/3mo/6mo/1y = daily; 3y/5y = weekly/monthly
  const apiRange = useMemo(() => {
    if (chartRange === 'since_first_buy' && firstBuyDate) {
      const daysSince = Math.ceil(
        (Date.now() - new Date(firstBuyDate).getTime()) / 86_400_000
      );
      if (daysSince <= 30) return '1mo';
      if (daysSince <= 90) return '90d';
      if (daysSince <= 180) return '180d';
      if (daysSince <= 365) return '1y';
      if (daysSince <= 730) return '3y'; // weekly granularity
      return '5y';
    }
    // Map 7d to Yahoo Finance's 5d range for 30-min granularity
    if (chartRange === '7d') return '5d';
    return chartRange;
  }, [chartRange, firstBuyDate]);

  useEffect(() => {
    if (!ticker) return;
    fetchChartData(ticker, apiRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker, apiRange]);

  const stats = useMemo(() => {
    const buys = holdingTransactions.filter((tx) => tx.transactionType === 0);
    const sells = holdingTransactions.filter((tx) => tx.transactionType === 1);

    const buyQuantityTotal = buys.reduce((sum, tx) => sum + Number(tx.quantity || 0), 0);
    const sellQuantityTotal = sells.reduce((sum, tx) => sum + Number(tx.quantity || 0), 0);

    const saleProceeds = sells.reduce((sum, tx) => sum + Number(tx.netAmount || 0), 0);

    const avgBuy =
      buyQuantityTotal > 0
        ? buys.reduce((sum, tx) => sum + Number(tx.grossAmount || 0), 0) / buyQuantityTotal
        : 0;

    return {
      buyQuantityTotal,
      sellQuantityTotal,
      saleProceeds,
      avgBuy,
      transactionCount: holdingTransactions.length,
    };
  }, [holdingTransactions]);

  const realizedPnlBase = Number(holding?.realizedPnlBase || 0);
  const unrealizedPnlBase = Number(holding?.unrealizedPnlBase || 0);
  const totalPnlBase = realizedPnlBase + unrealizedPnlBase;

  const realizedPnlNative = Number(holding?.realizedPnlNative || 0);
  const unrealizedPnlNative = Number(holding?.unrealizedPnlNative || 0);
  const totalPnlNative = realizedPnlNative + unrealizedPnlNative;

  // Treat floating-point dust (< 0.000001) as fully closed position
  const DUST_THRESHOLD = 1e-6;
  const rawOpenQuantity = Number(holding?.openQuantity || 0);
  const openQuantity = rawOpenQuantity < DUST_THRESHOLD ? 0 : rawOpenQuantity;
  const averageBuyPerUnit = Number(holding?.averageCostPerUnit || stats.avgBuy || 0);
  const investedCapital = openQuantity * averageBuyPerUnit;

  const parsedSellQuantity = Number(sellQuantity);
  const parsedSellPrice = Number(sellPrice);

  const sellPreview = useMemo(() => {
    const hasValidQuantity = Number.isFinite(parsedSellQuantity) && parsedSellQuantity > 0;
    const hasValidPrice = Number.isFinite(parsedSellPrice) && parsedSellPrice > 0;

    if (!hasValidQuantity || !hasValidPrice) {
      return {
        proceeds: 0,
        costBasis: 0,
        estimatedPnl: 0,
        remainingQuantity: openQuantity,
        isValid: false,
        exceedsOpenQuantity: false,
      };
    }

    const proceeds = parsedSellQuantity * parsedSellPrice;
    const costBasis = parsedSellQuantity * averageBuyPerUnit;
    const estimatedPnl = proceeds - costBasis;
    const remainingQuantity = openQuantity - parsedSellQuantity;
    const exceedsOpenQuantity = parsedSellQuantity > openQuantity;

    return {
      proceeds,
      costBasis,
      estimatedPnl,
      remainingQuantity,
      isValid: true,
      exceedsOpenQuantity,
    };
  }, [parsedSellPrice, parsedSellQuantity, averageBuyPerUnit, openQuantity]);

  const handleBuy = async (e) => {
    e.preventDefault();

    const parsedQuantity = Number(buyQuantity);
    const parsedPrice = Number(buyPrice);
    const normalizedTicker = String(ticker || '').toUpperCase();

    if (!parsedQuantity || parsedQuantity <= 0) {
      setError('Please enter a valid buy quantity.');
      return;
    }

    if (!parsedPrice || parsedPrice <= 0) {
      setError('Please enter a valid buy price.');
      return;
    }

    if (!buyDate) {
      setError('Please enter a buy date.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccessMessage('');

      const fxRateToBase = instrumentCurrency === reportCurrency ? 1 : 0;

      const response = await apiFetch(`/api/PortfolioTransactions`, {
        method: 'POST',
        body: JSON.stringify({
          portfolioId: Number(portfolioId),
          instrumentId: holding?.instrumentId || null,
          ticker: normalizedTicker,
          displayName: holding?.displayName || normalizedTicker,
          transactionType: 0,
          quantity: parsedQuantity,
          pricePerUnit: parsedPrice,
          tradeCurrency: instrumentCurrency,
          quoteCurrency: instrumentCurrency,
          fxRateToBase,
          feeAmount: 0,
          occurredAt: new Date(`${buyDate}T${buyTime || '12:00'}:00`).toISOString(),
          notes: buyNotes || '',
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error('Failed to create buy transaction. ' + errorText);
      }

      setBuyQuantity('');
      setBuyPrice('');
      setBuyDate('');
      setBuyTime('12:00');
      setBuyNotes('');

      await refreshData();
      showSuccess('Buy transaction was created successfully.');
    } catch (err) {
      setError(err.message || 'Failed to create buy transaction.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSell = async (e) => {
    e.preventDefault();

    const parsedQuantity = Number(sellQuantity);
    const parsedPrice = Number(sellPrice);
    const normalizedTicker = String(ticker || '').toUpperCase();

    if (!parsedQuantity || parsedQuantity <= 0) {
      setError('Please enter a valid sell quantity.');
      return;
    }

    if (!parsedPrice || parsedPrice <= 0) {
      setError('Please enter a valid sell price.');
      return;
    }

    if (!sellDate) {
      setError('Please enter a sell date.');
      return;
    }

    if (holding?.openQuantity != null && parsedQuantity > Number(holding.openQuantity)) {
      setError('You cannot sell more than the currently open quantity.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccessMessage('');

      const fxRateToBase = instrumentCurrency === reportCurrency ? 1 : 0;

      const response = await apiFetch(`/api/PortfolioTransactions`, {
        method: 'POST',
        body: JSON.stringify({
          portfolioId: Number(portfolioId),
          instrumentId: holding?.instrumentId || null,
          ticker: normalizedTicker,
          displayName: holding?.displayName || normalizedTicker,
          transactionType: 1,
          quantity: parsedQuantity,
          pricePerUnit: parsedPrice,
          tradeCurrency: instrumentCurrency,
          quoteCurrency: instrumentCurrency,
          fxRateToBase,
          feeAmount: 0,
          occurredAt: new Date(`${sellDate}T${sellTime || '12:00'}:00`).toISOString(),
          notes: sellNotes || '',
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error('Failed to create sell transaction. ' + errorText);
      }

      setSellQuantity('');
      setSellPrice('');
      setSellDate('');
      setSellTime('12:00');
      setSellNotes('');

      await refreshData();
      showSuccess('Sell transaction was created successfully.');
    } catch (err) {
      setError(err.message || 'Failed to create sell transaction.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateTransaction = async (transactionId, payload) => {
    setError('');
    setSuccessMessage('');

    const response = await apiFetch(
      `/api/PortfolioTransactions/${transactionId}`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error('Failed to update transaction. ' + errorText);
    }

    await refreshData();
    showSuccess('Transaction was updated successfully.');
  };

  const handleDeleteTransaction = async (transactionId) => {
    const confirmed = window.confirm(
      'Are you sure you want to delete this transaction? This action cannot be undone.'
    );

    if (!confirmed) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccessMessage('');

      const response = await apiFetch(
        `/api/PortfolioTransactions/${transactionId}`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error('Failed to delete transaction. ' + errorText);
      }

      await refreshData();
      showSuccess('Transaction was deleted successfully.');
    } catch (err) {
      setError(err.message || 'Failed to delete transaction.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>Loading holding detail...</div>
      </div>
    );
  }

  if (!holding) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>Holding was not found.</div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>

      {error && <div style={uiStyles.errorBox}>{error}</div>}
      {successMessage && <div style={successBoxStyle}>{successMessage}</div>}

      <HoldingChart
        ticker={ticker}
        displayName={holding.displayName || holding.ticker}
        loading={chartLoading}
        points={chartPoints}
        range={chartRange}
        onChangeRange={setChartRange}
        instrumentCurrency={instrumentCurrency}
        transactions={holdingTransactions}
        firstBuyDate={firstBuyDate}
        lineColorOverride={localColor || null}
      />

      <section style={heroCardStyle}>
        <div>
          <h1 style={heroTitleStyle}>{holding.displayName || holding.ticker}</h1>
          <p style={heroSubtitleStyle}>
            {holding.ticker} &bull; {localAssetType || holding.assetType} &bull; {instrumentCurrency}
          </p>
        </div>

        <div style={heroStatsStyle}>
          <MetricCard
            label="Open quantity"
            primaryValue={formatNumber(holding.openQuantity)}
          />

          <MetricCard
            label="Average buy"
            primaryValue={formatCurrency(
              holding.averageCostPerUnit || stats.avgBuy,
              instrumentCurrency
            )}
          />

          <MetricCard
            label="Current price"
            primaryValue={formatCurrency(holding.currentPrice, instrumentCurrency)}
          />

          <MetricCard
            label="Market value"
            primaryValue={formatCurrency(holding.marketValueNative, instrumentCurrency)}
            secondaryValue={formatCurrency(holding.marketValueBase, reportCurrency)}
          />

          <MetricCard
            label="Realized P/L"
            primaryValue={formatSignedCurrency(realizedPnlNative, instrumentCurrency)}
            secondaryValue={formatSignedCurrency(realizedPnlBase, reportCurrency)}
            tone={getTone(realizedPnlNative)}
          />

          <MetricCard
            label="Unrealized P/L"
            primaryValue={formatSignedCurrency(unrealizedPnlNative, instrumentCurrency)}
            secondaryValue={formatSignedCurrency(unrealizedPnlBase, reportCurrency)}
            tone={getTone(unrealizedPnlNative)}
          />

          <MetricCard
            label="Total P/L"
            primaryValue={formatSignedCurrency(totalPnlNative, instrumentCurrency)}
            secondaryValue={formatSignedCurrency(totalPnlBase, reportCurrency)}
            tone={getTone(totalPnlNative)}
          />

          <MetricCard
            label="Invested capital"
            primaryValue={formatCurrency(investedCapital, instrumentCurrency)}
          />

          <MetricCard
            label="Sale proceeds"
            primaryValue={formatCurrency(stats.saleProceeds, instrumentCurrency)}
          />

          <MetricCard
            label="Bought quantity"
            primaryValue={formatNumber(stats.buyQuantityTotal)}
          />

          <MetricCard
            label="Sold quantity"
            primaryValue={formatNumber(stats.sellQuantityTotal)}
          />

          <MetricCard
            label="Transactions"
            primaryValue={String(stats.transactionCount)}
          />
        </div>
      </section>

      <section style={formsGridStyle}>
        <section style={cardStyle}>
          <h3 style={sectionTitleStyle}>Buy</h3>

          <form onSubmit={handleBuy} style={formStyle}>
            <input
              type="number"
              step="0.00000001"
              placeholder="Quantity"
              value={buyQuantity}
              onChange={(e) => setBuyQuantity(e.target.value)}
              onBlur={(e) => {
                const v = parseFloat(e.target.value);
                if (!isNaN(v)) setBuyQuantity(String(parseFloat(v.toFixed(8))));
              }}
              style={formInputStyle}
            />

            <input
              type="number"
              step="any"
              placeholder={`Price (${instrumentCurrency})`}
              value={buyPrice}
              onChange={(e) => setBuyPrice(e.target.value)}
              style={formInputStyle}
            />

            <CustomDateInput
              value={buyDate}
              onChange={setBuyDate}
              placeholder="Select buy date"
            />

            <CustomTimeInput
              value={buyTime}
              onChange={setBuyTime}
            />

            <input
              type="text"
              placeholder="Notes (optional)"
              value={buyNotes}
              onChange={(e) => setBuyNotes(e.target.value)}
              style={formInputStyle}
            />

            <button type="submit" style={buyButtonStyle} disabled={submitting}>
              {submitting ? 'Saving...' : 'Buy'}
            </button>
          </form>
        </section>

        <section style={cardStyle}>
          <h3 style={sectionTitleStyle}>Sell</h3>

          <form onSubmit={handleSell} style={formStyle}>
            <div>
              <input
                type="number"
                step="0.00000001"
                placeholder={`Available: ${formatNumber(holding.openQuantity)} ${holding.ticker}`}
                value={sellQuantity}
                onChange={(e) => setSellQuantity(e.target.value)}
                onBlur={(e) => {
                  const v = parseFloat(e.target.value);
                  if (!isNaN(v)) setSellQuantity(String(parseFloat(v.toFixed(8))));
                }}
                style={{
                  ...formInputStyle,
                  ...(sellPreview.exceedsOpenQuantity ? { borderColor: '#ef4444', boxShadow: '0 0 0 2px rgba(239,68,68,0.18)' } : {}),
                }}
              />
              {sellPreview.exceedsOpenQuantity && (
                <div style={exceedWarningStyle}>
                  ⚠ Zadané množství ({formatNumber(parsedSellQuantity)}) překračuje dostupné ({formatNumber(openQuantity)} {holding.ticker})
                </div>
              )}
            </div>

            <input
              type="number"
              step="any"
              placeholder={`Price (${instrumentCurrency})`}
              value={sellPrice}
              onChange={(e) => setSellPrice(e.target.value)}
              style={formInputStyle}
            />

            <CustomDateInput
              value={sellDate}
              onChange={setSellDate}
              placeholder="Select sell date"
            />

            <CustomTimeInput
              value={sellTime}
              onChange={setSellTime}
            />

            <input
              type="text"
              placeholder="Notes (optional)"
              value={sellNotes}
              onChange={(e) => setSellNotes(e.target.value)}
              style={formInputStyle}
            />

            <button
              type="submit"
              style={dangerButtonStyle}
              disabled={submitting || sellPreview.exceedsOpenQuantity}
            >
              {submitting ? 'Saving...' : 'Sell'}
            </button>
          </form>

          <div style={{ ...previewCardStyle, display: 'none' }}>
            <div style={previewTitleStyle}>Sell preview</div>

            <div style={previewGridStyle}>
              <div style={previewItemStyle}>
                <span style={previewLabelStyle}>Estimated proceeds</span>
                <strong style={previewValueStyle}>
                  {sellPreview.isValid
                    ? formatCurrency(sellPreview.proceeds, instrumentCurrency)
                    : '-'}
                </strong>
              </div>

              <div style={previewItemStyle}>
                <span style={previewLabelStyle}>Estimated cost basis</span>
                <strong style={previewValueStyle}>
                  {sellPreview.isValid
                    ? formatCurrency(sellPreview.costBasis, instrumentCurrency)
                    : '-'}
                </strong>
              </div>

              <div style={previewItemStyle}>
                <span style={previewLabelStyle}>Estimated realized P/L</span>
                <strong
                  style={{
                    ...previewValueStyle,
                    color:
                      !sellPreview.isValid
                        ? 'var(--text-primary)'
                        : sellPreview.estimatedPnl > 0
                        ? 'var(--success)'
                        : sellPreview.estimatedPnl < 0
                        ? 'var(--danger)'
                        : 'var(--text-primary)',
                  }}
                >
                  {sellPreview.isValid
                    ? formatSignedCurrency(sellPreview.estimatedPnl, instrumentCurrency)
                    : '-'}
                </strong>
              </div>

              <div style={previewItemStyle}>
                <span style={previewLabelStyle}>Remaining quantity</span>
                <strong
                  style={{
                    ...previewValueStyle,
                    color: sellPreview.exceedsOpenQuantity
                      ? 'var(--danger)'
                      : 'var(--text-primary)',
                  }}
                >
                  {sellPreview.isValid
                    ? formatNumber(sellPreview.remainingQuantity)
                    : formatNumber(openQuantity)}
                </strong>
              </div>
            </div>

            {sellPreview.exceedsOpenQuantity ? (
              <div style={previewWarningStyle}>
                Sell quantity exceeds the current open quantity.
              </div>
            ) : null}
          </div>
        </section>
      </section>

      {/* ── Position settings ── */}
      <section style={cardStyle}>
        <h3 style={sectionTitleStyle}>Position settings</h3>
        <div style={posSettingsGridStyle}>

          {/* Asset type */}
          <div style={posSettingsFieldStyle}>
            <span style={posSettingsLabelStyle}>Asset type</span>
            <CustomSelect
              value={localAssetType || 'Stock'}
              onChange={handleAssetTypeChange}
              options={ASSET_TYPE_OPTIONS}
            />
            <span style={posSettingsHintStyle}>
              Určuje kategorii v grafu Portfolio Allocation.
            </span>
          </div>

          {/* Color */}
          <div style={posSettingsFieldStyle}>
            <span style={posSettingsLabelStyle}>Color</span>
            <div style={colorPickerRowStyle}>
              <label style={colorSwatchLabelStyle}>
                <input
                  type="color"
                  value={localColor || '#f472b6'}
                  onChange={(e) => handleColorChange(e.target.value)}
                  style={colorInputStyle}
                />
                <span
                  style={{
                    ...colorSwatchStyle,
                    background: localColor || '#f472b6',
                  }}
                />
              </label>
              <span style={colorHexStyle}>{localColor || '#f472b6'}</span>
              {localColor && (
                <button
                  type="button"
                  onClick={() => handleColorChange('')}
                  style={colorResetButtonStyle}
                  title="Obnovit výchozí barvu"
                >
                  ↺ Reset
                </button>
              )}
            </div>
            <span style={posSettingsHintStyle}>
              Barva v grafu nákupů a v Portfolio Allocation.
            </span>
          </div>

        </div>
      </section>

      <HoldingTransactionsTable
        title="Dividend Transactions"
        storageKey={portfolioId && ticker ? `txDiv-${portfolioId}-${ticker}` : null}
        loading={transactionsLoading}
        transactions={holdingTransactions.filter((tx) => tx.transactionType === 2)}
        onDeleteTransaction={handleDeleteTransaction}
        onUpdateTransaction={handleUpdateTransaction}
      />
      <HoldingTransactionsTable
        title="Buy Transactions"
        storageKey={portfolioId && ticker ? `txBuy-${portfolioId}-${ticker}` : null}
        loading={transactionsLoading}
        transactions={holdingTransactions.filter((tx) => tx.transactionType === 0)}
        onDeleteTransaction={handleDeleteTransaction}
        onUpdateTransaction={handleUpdateTransaction}
        currentPrice={holding?.currentPrice}
        instrumentCurrency={instrumentCurrency}
      />
      <HoldingTransactionsTable
        title="Sell Transactions"
        storageKey={portfolioId && ticker ? `txSell-${portfolioId}-${ticker}` : null}
        loading={transactionsLoading}
        transactions={holdingTransactions.filter((tx) => tx.transactionType === 1)}
        onDeleteTransaction={handleDeleteTransaction}
        onUpdateTransaction={handleUpdateTransaction}
        currentPrice={holding?.currentPrice}
        instrumentCurrency={instrumentCurrency}
      />
    </div>
  );
}

function MetricCard({
  label,
  primaryValue,
  secondaryValue,
  tone = 'default',
}) {
  return (
    <div style={statItemStyle}>
      <span style={statLabelStyle}>{label}</span>
      <strong
        style={{
          ...statValueStyle,
          color: getColorForTone(tone),
        }}
      >
        {primaryValue}
      </strong>
      {secondaryValue ? (
        <span style={statSecondaryValueStyle}>{secondaryValue}</span>
      ) : null}
    </div>
  );
}

function getTone(value) {
  const numeric = Number(value || 0);

  if (numeric > 0) return 'positive';
  if (numeric < 0) return 'negative';
  return 'default';
}

function getColorForTone(tone) {
  if (tone === 'positive') return 'var(--success)';
  if (tone === 'negative') return 'var(--danger)';
  return 'var(--text-primary)';
}

function formatCurrency(value, currency) {
  try {
    return new Intl.NumberFormat('cs-CZ', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 2,
    }).format(Number(value || 0));
  } catch {
    return Number(value || 0).toFixed(2) + ' ' + (currency || '');
  }
}

function formatSignedCurrency(value, currency) {
  const numericValue = Number(value || 0);
  const formatted = formatCurrency(Math.abs(numericValue), currency);

  if (numericValue > 0) return '+' + formatted;
  if (numericValue < 0) return '-' + formatted;
  return formatted;
}

function formatNumber(value) {
  const n = Number(value || 0);
  // Count natural (non-trailing-zero) decimal places in the JS float representation
  const natural = n.toString();
  const naturalDecimals = (natural.includes('.') ? natural.split('.')[1] : '').length;
  // Precise crypto amounts (≥5 decimal places) always show full 8 places
  // Round values (e.g. 1.5, 2, 10) show natural precision without trailing zeros
  const minFrac = naturalDecimals >= 5 ? 8 : 0;
  return new Intl.NumberFormat('cs-CZ', {
    minimumFractionDigits: minFrac,
    maximumFractionDigits: 8,
  }).format(n);
}

const pageStyle = {
  display: 'grid',
  gap: '24px',
};

const heroCardStyle = {
  background: 'var(--surface-strong)',
  borderRadius: '18px',
  padding: '24px',
  boxShadow: 'var(--shadow)',
  border: '1px solid var(--border)',
  display: 'grid',
  gap: '20px',
};

const heroTitleStyle = {
  margin: 0,
  color: 'var(--text-primary)',
};

const heroSubtitleStyle = {
  margin: '8px 0 0 0',
  color: 'var(--text-secondary)',
};

const heroStatsStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))',
  gap: '16px',
  minWidth: 0,
};

const statItemStyle = {
  display: 'grid',
  gap: '6px',
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: '14px',
  padding: '16px',
};

const statLabelStyle = {
  color: 'var(--text-secondary)',
  fontSize: '13px',
  fontWeight: 600,
};

const statValueStyle = {
  color: 'var(--text-primary)',
  fontSize: '20px',
  fontWeight: 800,
};

const statSecondaryValueStyle = {
  color: 'var(--text-secondary)',
  fontSize: '13px',
  fontWeight: 600,
};

const formsGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))',
  gap: '20px',
  minWidth: 0,
};

const cardStyle = {
  background: 'var(--surface-strong)',
  borderRadius: '18px',
  padding: '24px',
  boxShadow: 'var(--shadow)',
  border: '1px solid var(--border)',
};

const sectionTitleStyle = {
  marginTop: 0,
  marginBottom: '16px',
  color: 'var(--text-primary)',
};

const formStyle = {
  display: 'grid',
  gap: '12px',
};

const formInputStyle = {
  ...uiStyles.input,
  borderRadius: '12px',
  border: '1px solid var(--border)',
  minHeight: '48px',
  fontSize: '14px',
  boxSizing: 'border-box',
};


const previewCardStyle = {
  marginTop: '16px',
  padding: '16px',
  borderRadius: '14px',
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  display: 'grid',
  gap: '14px',
};

const previewTitleStyle = {
  color: 'var(--text-primary)',
  fontSize: '15px',
  fontWeight: 800,
};

const previewGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))',
  gap: '12px',
  minWidth: 0,
};

const previewItemStyle = {
  display: 'grid',
  gap: '6px',
};

const previewLabelStyle = {
  color: 'var(--text-secondary)',
  fontSize: '12px',
  fontWeight: 700,
};

const previewValueStyle = {
  color: 'var(--text-primary)',
  fontSize: '16px',
  fontWeight: 800,
};

const previewWarningStyle = {
  padding: '10px 12px',
  borderRadius: '10px',
  background: 'rgba(220, 38, 38, 0.12)',
  color: '#fca5a5',
  border: '1px solid rgba(220, 38, 38, 0.35)',
  fontWeight: 600,
  fontSize: '13px',
};

const successBoxStyle = {
  marginBottom: '16px',
  padding: '12px 14px',
  borderRadius: '12px',
  background: 'rgba(34, 197, 94, 0.12)',
  color: '#86efac',
  border: '1px solid rgba(34, 197, 94, 0.35)',
  fontWeight: 600,
};

const buyButtonStyle = {
  border: 'none',
  borderRadius: '12px',
  padding: '12px 16px',
  fontWeight: 700,
  cursor: 'pointer',
  background: 'var(--success)',
  color: '#ffffff',
};

const dangerButtonStyle = {
  border: 'none',
  borderRadius: '12px',
  padding: '12px 16px',
  fontWeight: 700,
  cursor: 'pointer',
  background: '#dc2626',
  color: '#ffffff',
};

const exceedWarningStyle = {
  marginTop: '6px',
  padding: '8px 12px',
  borderRadius: '10px',
  background: 'rgba(239,68,68,0.12)',
  border: '1px solid rgba(239,68,68,0.35)',
  color: '#fca5a5',
  fontSize: '13px',
  fontWeight: 600,
};

const posSettingsGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))',
  gap: '20px',
  alignItems: 'start',
};

const posSettingsFieldStyle = {
  display: 'grid',
  gap: '6px',
};

const posSettingsLabelStyle = {
  fontSize: '12px',
  fontWeight: 700,
  color: 'var(--text-secondary)',
};

const posSettingsHintStyle = {
  fontSize: '12px',
  color: 'var(--text-secondary)',
  lineHeight: 1.45,
};

const colorPickerRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
};

const colorSwatchLabelStyle = {
  position: 'relative',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  width: '40px',
  height: '40px',
};

const colorInputStyle = {
  position: 'absolute',
  inset: 0,
  opacity: 0,
  width: '100%',
  height: '100%',
  cursor: 'pointer',
  padding: 0,
  margin: 0,
  border: 'none',
};

const colorSwatchStyle = {
  display: 'block',
  width: '40px',
  height: '40px',
  borderRadius: '10px',
  border: '2px solid var(--border)',
  pointerEvents: 'none',
};

const colorHexStyle = {
  fontSize: '13px',
  fontFamily: 'monospace',
  color: 'var(--text-secondary)',
  userSelect: 'all',
};

const colorResetButtonStyle = {
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  background: 'var(--surface-soft)',
  border: '1px solid var(--border)',
  borderRadius: '8px',
  padding: '4px 10px',
  cursor: 'pointer',
};

export default HoldingDetailPage;